import { type TFunction } from 'i18next';
import { flatten } from 'lodash';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { BladeSide } from './BladeDamagePlotContainer';
import { getLabelParametersFromRangePath } from './pointUtils';

export type SelectionRangeProps = {
  distance: [number, number];
  chord?: [number, number];
  rawSelection: [[number, number], [number, number]];
  xScale: d3.ScaleLinear<number, number, never>;
  yScale: d3.ScaleLinear<number, number, never>;
  shapePoints: [number, number][];
  bladeLength: number;
  bladeSide: BladeSide;
  hideLabel?: boolean;
};

// Half-thickness of the leading/trailing edge band, which has no chord range to follow. Fixed
// rather than proportional because `EdgeShape` it sits on also renders at a fixed thickness.
const EDGE_BAND_HALF_HEIGHT = 6;

// The selected distance and chord ranges as text, for callers that show them outside the plot.
export const getSelectionRangeLabels = (
  t: TFunction,
  distance: [number, number],
  chord?: [number, number]
) => {
  const distLabel = t('damage.selectionRange.distLabel', 'dist: {{from}}m to {{to}}m', {
    from: distance[0].toFixed(1),
    to: distance[1].toFixed(1),
  });
  const chordLabel = chord
    ? t('damage.selectionRange.chordLabel', 'chord: {{from}}% to {{to}}%', {
        from: chord[0].toFixed(0),
        to: chord[1].toFixed(0),
      })
    : null;

  return { distLabel, chordLabel };
};

/**
 * Given the selection drawn by a D3 Brush, redraws the box to match the blade
 * shape and contour, and adds a label of the selected distance and chord ranges
 */
export const SelectionRange: React.FunctionComponent<SelectionRangeProps> = ({
  distance,
  chord,
  xScale,
  yScale,
  rawSelection,
  shapePoints,
  bladeLength,
  bladeSide,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const textRef = useRef<SVGTextElement>(null);
  const yRange = useMemo(() => yScale.range(), [yScale]);

  // Centred on the mid-chord line edge Damages plot onto (`yScale(50)` in `pointUtils`), so the
  // band tracks whatever height the caller renders at instead of assuming the plot's own 100.
  const edgeBandTop = yScale(50) - EDGE_BAND_HALF_HEIGHT;
  const edgeBandBottom = yScale(50) + EDGE_BAND_HALF_HEIGHT;

  // Subset of points for the blade shape curve at the selected distance range
  const shapePointsSubset = useMemo(
    () =>
      shapePoints.slice(
        Math.round((100 * distance[0]) / bladeLength),
        Math.round((100 * distance[1]) / bladeLength) + 1
      ),
    [shapePoints, distance, bladeLength]
  );

  // Derived from props rather than read back off the rendered path, so the label is placed on
  // the same render that draws the box. A ref read is a render behind, and a caller that renders
  // once would leave the label on its top-left fallback.
  const boxPath = useMemo(() => {
    if (chord && bladeSide === BladeSide.PressureSide) {
      return [
        ...flatten(
          shapePointsSubset.map((point, i) => [
            i === 0 ? 'M' : 'L',
            xScale(point[0]),
            yScale(point[1]) + ((100 - chord[0]) / 100) * (yRange[0] - yScale(point[1])),
          ])
        ),
        ...flatten(
          [...shapePointsSubset]
            .reverse()
            .map((point) => [
              'L',
              xScale(point[0]),
              yScale(point[1]) + ((100 - chord[1]) / 100) * (yRange[0] - yScale(point[1])),
            ])
        ),
        'Z',
      ].join(' ');
    }

    if (chord && bladeSide === BladeSide.SuctionSide) {
      return [
        ...flatten(
          shapePointsSubset.map((point, i) => [
            i === 0 ? 'M' : 'L',
            xScale(point[0]),
            (chord[1] / 100) * yScale(point[1]),
          ])
        ),
        ...flatten(
          [...shapePointsSubset]
            .reverse()
            .map((point) => ['L', xScale(point[0]), (chord[0] / 100) * yScale(point[1])])
        ),
        'Z',
      ].join(' ');
    }

    if ([BladeSide.LeadingEdge, BladeSide.TrailingEdge].includes(bladeSide)) {
      return [
        'M',
        rawSelection[0][0],
        edgeBandTop,
        'L',
        rawSelection[1][0],
        edgeBandTop,
        'L',
        rawSelection[1][0],
        edgeBandBottom,
        'L',
        rawSelection[0][0],
        edgeBandBottom,
        'Z',
      ].join(' ');
    }

    return null;
  }, [
    chord,
    bladeSide,
    shapePointsSubset,
    xScale,
    yScale,
    yRange,
    rawSelection,
    edgeBandTop,
    edgeBandBottom,
  ]);

  const labelParameters = getLabelParametersFromRangePath(boxPath);

  const { distLabel, chordLabel } = getSelectionRangeLabels(t, distance, chord);

  // Text metrics need a real measurement, so the backdrop lands one render later. `useLayoutEffect`
  // keeps that render pre-paint, and `getBBox` is guarded: jsdom doesn't implement it, and Firefox
  // throws on it in a subtree that isn't rendered.
  const [textBoundingBox, setTextBoundingBox] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    const node = textRef.current;
    if (hideLabel || !node || typeof node.getBBox !== 'function') {
      return;
    }

    try {
      const { x, y, width, height } = node.getBBox();
      setTextBoundingBox(new DOMRect(x, y, width, height));
    } catch {
      // Unrendered subtree — leave the backdrop collapsed rather than crash the plot.
    }
    // The box only moves when the text or its anchoring changes; the font is fixed in CSS.
  }, [hideLabel, distLabel, chordLabel, labelParameters.textAnchor]);

  const textBackgroundAttributes = useMemo(() => {
    if (textBoundingBox) {
      return {
        x: textBoundingBox.x - 4,
        y: textBoundingBox.y - 2,
        width: textBoundingBox.width + 8,
        height: textBoundingBox.height + 4,
      };
    }

    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
  }, [textBoundingBox]);

  return (
    <>
      {boxPath && <path className="selection-range" d={boxPath} />}
      {!hideLabel && (
        <>
          <rect
            transform={`translate(${labelParameters.x}, ${labelParameters.y})`}
            fill={theme.secondaryBackgroundColor}
            opacity={0.6}
            rx={4}
            ry={4}
            {...textBackgroundAttributes}
          />
          <text
            ref={textRef}
            className="selection-range-label"
            transform={`translate(${labelParameters.x}, ${labelParameters.y})`}
            style={{ textAnchor: labelParameters.textAnchor }}
            fill={theme.plot.axisColor}
          >
            <tspan x="0" dy="1em">
              {distLabel}
            </tspan>

            {chordLabel && (
              <tspan x="0" dy="1em">
                {chordLabel}
              </tspan>
            )}
          </text>
        </>
      )}
    </>
  );
};
